<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { useSidebarMenu } from "@/composables/useSidebarMenu";

defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const route = useRoute();

const expandedMenus = ref({});

const user = computed(() => store.getters.getUser);

const { sidebarList } = useSidebarMenu();

const isRouteMatch = (routeName, routerLink) => {
  return routeName === routerLink || routeName.startsWith(`${routerLink}.`);
};

const isItemActive = (item) => {
  if (!item || !item.routerLink || !route.name) {
    return false;
  }
  return isRouteMatch(route.name, item.routerLink);
};

const isParentActive = (parent) => {
  if (!parent.children || !route.name) {
    return false;
  }
  return parent.children.some((child) => isRouteMatch(route.name, child.routerLink));
};

const isMenuExpanded = (menuId) => {
  return expandedMenus.value[menuId] || false;
};

const handleToggleMenu = (menuId) => {
  expandedMenus.value[menuId] = !expandedMenus.value[menuId];
};

const checkAndExpandActiveMenus = () => {
  sidebarList.value
    .filter((item) => item.isParent)
    .forEach((parent) => {
      if (isParentActive(parent)) {
        expandedMenus.value[parent.id] = true;
      }
    });
};

onMounted(() => {
  checkAndExpandActiveMenus();
});

watch(
  () => route.name,
  () => {
    checkAndExpandActiveMenus();
  },
);
</script>

<template>
  <div>
    <div v-if="user.id" id="sidebar-menu" :class="{ collapsed: collapsed }">
      <template v-for="(item, index) in sidebarList" :key="index">
        <template v-if="item.isParent">
          <div
            class="sidebar-list-item cursor-pointer"
            :class="{ 'sidebar-list-item-parent-active': isParentActive(item) }"
            :title="item.text"
            @click="handleToggleMenu(item.id)"
          >
            <div class="sidebar-list-item-icon">
              <i :class="item.icon" />
            </div>
            <div class="sidebar-list-item-text">
              {{ item.text }}
            </div>
            <div class="sidebar-list-item-chevron">
              <i class="bi-chevron-right" :class="{ rotated: isMenuExpanded(item.id) }" />
            </div>
          </div>

          <div v-show="isMenuExpanded(item.id)" class="sidebar-submenu">
            <router-link
              v-for="(child, childIndex) in item.children"
              :key="childIndex"
              :to="{ name: child.routerLink }"
              :title="child.text"
            >
              <div
                class="sidebar-list-item sidebar-list-item-child"
                :class="{ 'sidebar-list-item-active': isItemActive(child) }"
              >
                <div class="sidebar-list-item-icon">
                  <i :class="child.icon" />
                </div>
                <div class="sidebar-list-item-text">
                  {{ child.text }}
                </div>
              </div>
            </router-link>
          </div>
        </template>

        <router-link v-else :to="{ name: item.routerLink }" :title="item.text">
          <div class="sidebar-list-item" :class="{ 'sidebar-list-item-active': isItemActive(item) }">
            <div class="sidebar-list-item-icon">
              <i :class="item.icon" />
            </div>
            <div class="sidebar-list-item-text">
              {{ item.text }}
            </div>
          </div>
        </router-link>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#sidebar-menu {
  font-size: 1em;
  color: oklch(var(--bc));
  margin: 0px 0.5rem;
  transition: background-color 0.2s ease;

  .sidebar-list-item {
    padding: 12px 16px 12px 8px;
    margin: 4px 0px;
    border-radius: 6px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    overflow: hidden;

    &-icon {
      font-size: 1.2em;
      color: oklch(var(--bc));
      transition: color 0.2s ease;
      min-width: 40px;
      display: flex;
      justify-content: center;
    }

    &-text {
      color: oklch(var(--bc));
      white-space: nowrap;
      opacity: 1;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      transition:
        opacity 0.2s ease,
        flex 0.2s ease;
    }

    &-chevron {
      font-size: 0.8em;
      color: oklch(var(--bc));
      overflow: hidden;
      transition:
        transform 0.2s ease,
        opacity 0.2s ease,
        width 0.2s ease,
        margin 0.2s ease;
      margin-right: 8px;

      i {
        display: block;
        transition: transform 0.2s ease;

        &.rotated {
          transform: rotate(90deg);
        }
      }
    }

    &-child {
      padding: 8px;
      margin: 2px 0;
      font-size: 0.9em;

      .sidebar-list-item-icon {
        font-size: 1em;
        min-width: 32px;
      }
    }

    &-active {
      @apply bg-primary bg-opacity-15;

      .sidebar-list-item-icon,
      .sidebar-list-item-text {
        @apply text-primary;
      }

      &.sidebar-list-item-child {
        @apply bg-primary bg-opacity-10;
      }
    }

    &-parent-active {
      .sidebar-list-item-icon,
      .sidebar-list-item-text,
      .sidebar-list-item-chevron {
        @apply text-primary;
      }
    }

    &:hover {
      &:not(.sidebar-list-item-active) {
        background-color: color-mix(in oklch, oklch(var(--p)) 5%, transparent);
      }
    }
  }

  .sidebar-submenu {
    overflow: hidden;
    position: relative;
    margin-left: 27px;
    padding-left: 12px;
    border-left: 1px solid oklch(var(--bc) / 0.2);
  }

  &.collapsed {
    margin: 0 0.5rem;

    .sidebar-list-item {
      justify-content: center;
      padding: 12px;
      margin: 4px 0;

      &-icon {
        font-size: 1.4em;
        min-width: auto;
      }

      &-text {
        opacity: 0;
        flex: 0;
        width: 0;
        overflow: hidden;
      }

      &-chevron {
        opacity: 0;
        width: 0;
        overflow: hidden;
        margin-right: 0;
      }

      &-child {
        padding-left: 12px;
      }

      &-active,
      &-parent-active {
        background-color: color-mix(in oklch, oklch(var(--p)) 15%, transparent);

        .sidebar-list-item-icon {
          color: oklch(var(--p));
        }
      }

      &:hover:not(.sidebar-list-item-active):not(.sidebar-list-item-parent-active) {
        background-color: color-mix(in oklch, oklch(var(--p)) 10%, transparent);
      }
    }

    .sidebar-submenu {
      display: none;
    }
  }
}
</style>
